import React from "react";
import { StyleSheet, View } from "react-native";
import { FormFieldProps } from "../FormFieldProps";
import { Button, IconButton, Text, useTheme } from "react-native-paper";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { RootStackParamList } from "../../types/navigationParams";
import { useTranslation } from "react-i18next";
import { CollectionPickerOptions } from "../../screens/rentals/CollectionPickerScreen";
import { OutlinedInput } from "./OutlinedInput";

import {Field} from "../../config/companies/config";

interface WarehouseItem {
  name: string;
  quantity: number;
}

type WarehouseItems = {
  [itemId: string]: WarehouseItem;
};

type Props = Omit<FormFieldProps, "value"> & {
  value: WarehouseItems;
  field: Field;
};

export const WarehouseItemsField = ({
  field,
  value,
  onChange,
  formData,
}: Props) => {
  const navigation = useNavigation<NavigationProp<RootStackParamList>>();
  const { t } = useTranslation();
  const theme = useTheme();

  // Ensure value is always an object (assumes correct structure or undefined)
  const normalizedValue: WarehouseItems = value || {};

  const itemsArray = Object.entries(normalizedValue).map(([itemId, item]) => ({
    itemId,
    ...item,
  }));

  const openPicker = () => {
    navigation.navigate("CollectionPickerScreen", {
      onValueChange: (newItem: any) => {
        const itemId = newItem.id;
        const itemName = newItem[field.key!];

        // Add new item with quantity 1 if it doesn't exist
        if (!normalizedValue[itemId]) {
          onChange(field?.name, {
            ...normalizedValue,
            [itemId]: {
              name: itemName,
              quantity: 1,
            },
          });
        }
      },
      formData: formData,
      collection: field.collection,
      key: field.key,
      secondKey: field.secondKey,
    } as CollectionPickerOptions);
  };

  const removeItem = (itemId: string) => {
    const newValue = { ...normalizedValue };
    delete newValue[itemId];
    onChange(field?.name, newValue);
  };

  const updateQuantity = (itemId: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeItem(itemId);
      return;
    }

    onChange(field?.name, {
      ...normalizedValue,
      [itemId]: {
        ...normalizedValue[itemId],
        quantity: newQuantity,
      },
    });
  };

  const incrementQuantity = (itemId: string) => {
    const currentQuantity = normalizedValue[itemId]?.quantity || 0;
    updateQuantity(itemId, currentQuantity + 1);
  };

  const decrementQuantity = (itemId: string) => {
    const currentQuantity = normalizedValue[itemId]?.quantity || 0;
    updateQuantity(itemId, Math.max(0, currentQuantity - 1));
  };

  return (
    <View style={styles.container}>
      <View style={styles.headerContainer}>
        <Text variant="titleMedium">{t(field?.label)}</Text>
        <Button onPress={openPicker} mode="text">
          {t("Add")}
        </Button>
      </View>
      {itemsArray.map((item) => (
        <View key={item.itemId} style={styles.itemContainer}>
          <View style={styles.itemContent}>
            <View style={styles.itemHeader}>
              <View
                style={[
                  styles.chip,
                  { backgroundColor: theme.colors.secondaryContainer },
                ]}
              >
                <Text variant="bodyMedium">{item.name}</Text>
              </View>
              <View style={styles.actionsContainer}>
                <IconButton
                  style={{ borderRadius: 8 }}
                  mode="contained-tonal"
                  size={20}
                  icon="minus"
                  onPress={() => decrementQuantity(item.itemId)}
                />
                <OutlinedInput
                  onChangeText={(text) => {
                    const quantity =
                      parseInt(text.replace(/[^0-9]/g, ""), 10) || 0;
                    updateQuantity(item.itemId, quantity);
                  }}
                  value={item.quantity.toString()}
                  outlineStyle={{ borderRadius: 8 }}
                  style={{ height: 40, width: 60 }}
                  keyboardType="numeric"
                />
                <IconButton
                  size={20}
                  style={styles.counterButton}
                  icon="plus"
                  onPress={() => incrementQuantity(item.itemId)}
                  mode="contained-tonal"
                />
                <IconButton
                  style={styles.actionButton}
                  mode="contained-tonal"
                  size={20}
                  icon="close"
                  onPress={() => removeItem(item.itemId)}
                />
              </View>
            </View>
          </View>
        </View>
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: 8,
  },
  headerContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    paddingHorizontal: 4,
  },
  emptyContainer: {
    padding: 16,
    alignItems: "center",
  },
  itemContainer: {
    marginBottom: 8,
    borderRadius: 8,
    backgroundColor: "transparent",
  },
  itemContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  itemHeader: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  chip: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginRight: 8,
    flex: 1,
  },
  actionsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  actionButton: {
    margin: 0,
  },
  counterButton: {
    margin: 0,
  },
});
